/**
 * Process the policy embedding queue
 * This service processes queued embedding generation tasks
 */

import { supabase } from '@/integrations/supabase/client';

export interface QueueItem {
  id: string;
  policy_id: string;
  action: 'create' | 'update' | 'delete';
  created_at: string;
}

export interface ProcessResult {
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  errors: Array<{ id: string; policy_id: string; error: string }>;
}

/**
 * Call the Edge Function to process a single policy
 */
async function processPolicy(policyId: string, action: string, apiKey?: string): Promise<void> {
  // Get API key from localStorage if not provided
  const effectiveApiKey = apiKey || localStorage.getItem('openai_api_key');
  
  // Use Supabase's function invoke method (handles auth automatically)
  const { data, error } = await supabase.functions.invoke('generate-policy-embeddings', {
    body: {
      policy_id: policyId,
      action: action,
      api_key: effectiveApiKey || undefined
    }
  });

  if (error) {
    throw new Error(error.message || 'Failed to invoke Edge Function');
  }

  if (data && !data.success) {
    throw new Error(data.error || 'Edge Function returned error');
  }

  return data;
}

/**
 * Process all unprocessed items in the queue
 * 
 * @param batchSize - Maximum number of items to process in one run
 * @param onProgress - Optional callback for progress updates
 * @returns Processing results
 */
export async function processEmbeddingQueue(
  batchSize: number = 10,
  onProgress?: (progress: ProcessResult) => void
): Promise<ProcessResult> {
  const result: ProcessResult = {
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0,
    errors: []
  };

  try {
    // Fetch unprocessed queue items
    const { data: queueItems, error: fetchError } = await supabase
      .from('policy_embedding_queue')
      .select('id, policy_id, action, created_at')
      .eq('processed', false)
      .order('created_at', { ascending: true })
      .limit(batchSize);

    if (fetchError) {
      throw fetchError;
    }

    if (!queueItems || queueItems.length === 0) {
      console.log('No items in queue to process');
      return result;
    }

    result.total = queueItems.length;
    console.log(`Processing ${result.total} queued embedding tasks...`);

    // Get API key from localStorage (BYOK)
    const apiKey = localStorage.getItem('openai_api_key');
    
    if (!apiKey) {
      throw new Error('OpenAI API key is required. Please configure it in Settings → AI & Integration.');
    }

    // Process each item
    for (const item of queueItems) {
      try {
        console.log(`Processing: ${item.action} for policy ${item.policy_id}`);
        
        await processPolicy(item.policy_id, item.action, apiKey);
        
        // Mark as processed
        await supabase
          .from('policy_embedding_queue')
          .update({
            processed: true,
            processed_at: new Date().toISOString()
          })
          .eq('id', item.id);
        
        result.succeeded++;
        console.log(`✓ Completed: ${item.id}`);
      } catch (error: any) {
        console.error(`✗ Failed: ${item.id}`, error);
        
        result.failed++;
        result.errors.push({
          id: item.id,
          policy_id: item.policy_id,
          error: error.message
        });
        
        // Mark as processed with error
        await supabase
          .from('policy_embedding_queue')
          .update({
            processed: true,
            processed_at: new Date().toISOString(),
            error: error.message
          })
          .eq('id', item.id);
      } finally {
        result.processed++;
        
        if (onProgress) {
          onProgress({ ...result });
        }
        
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    console.log('\n=== Queue Processing Complete ===');
    console.log(`Total: ${result.total}`);
    console.log(`Succeeded: ${result.succeeded}`);
    console.log(`Failed: ${result.failed}`);

    return result;
  } catch (error: any) {
    console.error('Queue processing failed:', error);
    throw error;
  }
}

/**
 * Get queue statistics
 */
export async function getQueueStats() {
  try {
    const { data: unprocessed, error: unprocessedError } = await supabase
      .from('policy_embedding_queue')
      .select('id', { count: 'exact', head: true })
      .eq('processed', false);

    if (unprocessedError) throw unprocessedError;

    const { data: failed, error: failedError } = await supabase
      .from('policy_embedding_queue')
      .select('id', { count: 'exact', head: true })
      .eq('processed', true)
      .not('error', 'is', null);

    if (failedError) throw failedError;

    const { data: succeeded, error: succeededError } = await supabase
      .from('policy_embedding_queue')
      .select('id', { count: 'exact', head: true })
      .eq('processed', true)
      .is('error', null);

    if (succeededError) throw succeededError;

    return {
      unprocessed: unprocessed || 0,
      failed: failed || 0,
      succeeded: succeeded || 0,
      total: (unprocessed || 0) + (failed || 0) + (succeeded || 0)
    };
  } catch (error: any) {
    console.error('Error getting queue stats:', error);
    throw error;
  }
}

/**
 * Clear processed items from the queue
 * 
 * @param olderThanDays - Clear items older than X days (default: 7)
 */
export async function clearProcessedQueue(olderThanDays: number = 7): Promise<number> {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

    const { data, error } = await supabase
      .from('policy_embedding_queue')
      .delete()
      .eq('processed', true)
      .lt('processed_at', cutoffDate.toISOString())
      .select('id');

    if (error) throw error;

    const deletedCount = data?.length || 0;
    console.log(`Cleared ${deletedCount} processed queue items older than ${olderThanDays} days`);
    
    return deletedCount;
  } catch (error: any) {
    console.error('Error clearing queue:', error);
    throw error;
  }
}

/**
 * Manually trigger embedding generation for a policy
 * (bypasses the queue)
 */
export async function triggerEmbeddingGeneration(
  policyId: string,
  action: 'create' | 'update' | 'delete' = 'create'
): Promise<void> {
  // Get API key from localStorage (BYOK)
  const apiKey = localStorage.getItem('openai_api_key');
  
  if (!apiKey) {
    throw new Error('OpenAI API key is required. Please configure it in Settings → AI & Integration.');
  }
  
  return await processPolicy(policyId, action, apiKey);
}

